import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { formatPrice, formatDate } from '../../utils';
import { useAuth } from '../../contexts/AuthContext';
import adminAnalyticsService from '../../services/adminAnalyticsService';
import MetricsCard from './components/MetricsCard';
import UserManagementPanel from './components/UserManagementPanel';
import VendorManagementPanel from './components/VendorManagementPanel';
import ContentModerationPanel from './components/ContentModerationPanel';
import FinancialOverview from './components/FinancialOverview';
import SystemHealthPanel from './components/SystemHealthPanel';
import AnalyticsPanel from './components/AnalyticsPanel';
import SEOToolsPanel from './components/SEOToolsPanel';
import LocationManagementPanel from './components/LocationManagementPanel';
import PageManagementPanel from './components/PageManagementPanel';
import PaymentPlansPanel from './components/PaymentPlansPanel';
import NewsletterToolsPanel from './components/NewsletterToolsPanel';
import AdminRolesPanel from './components/AdminRolesPanel';
import CategoryManagementPanel from './components/CategoryManagementPanel';
import ListingManagementPanel from './components/ListingManagementPanel';
import BackupPanel from './components/BackupPanel';
import PaymentIntegrationPanel from './components/PaymentIntegrationPanel';
import CustomerSupportPanel from './components/CustomerSupportPanel';
import PushNotificationPanel from './components/PushNotificationPanel';
import BlogManagementPanel from './components/BlogManagementPanel';
import PopupManagerPanel from './components/PopupManagerPanel';
import TestimonialsPanel from './components/TestimonialsPanel';
import EmailMarketingPanel from './components/EmailMarketingPanel';
import EventPromotionsPanel from './components/EventPromotionsPanel';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [adminUser, setAdminUser] = useState(null);
  const [platformMetrics, setPlatformMetrics] = useState({
    totalUsers: 0,
    activeVendors: 0,
    monthlyBookings: 0,
    totalRevenue: 0,
    pendingVendors: 0,
    reportedContent: 0,
    systemUptime: 100,
    activeSessions: 0
  });

  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    loadAdminData();
  }, [user]);

  const loadAdminData = async () => {
    if (!user?.id) {
      navigate('/sign-in-page');
      return;
    }

    setLoading(true);

    // Load admin user profile
    setAdminUser({
      name: user.full_name || 'Admin User',
      email: user.email || 'admin@shaadinest.com',
      role: user.role === 'admin' ? 'Admin' : 'User',
      permissions: user.role === 'admin' ? ['all'] : []
    });

    // Check if user is actually an admin
    if (user.role !== 'admin') {
      navigate('/user-dashboard');
      return;
    }

    // Load platform metrics from adminAnalyticsService
    const statsResult = await adminAnalyticsService.getCurrentStats();
    if (statsResult.success) {
      setPlatformMetrics({
        totalUsers: statsResult.data.totalUsers || 0,
        activeVendors: statsResult.data.activeVendors || 0,
        monthlyBookings: statsResult.data.totalBookings || 0,
        totalRevenue: statsResult.data.totalRevenue || 0,
        pendingVendors: 0, // Will load separately
        reportedContent: 0, // Will load separately
        systemUptime: 99.9,
        activeSessions: 0
      });
    }

    // Load pending vendors
    const pendingResult = await adminAnalyticsService.getVendorApprovalQueue();
    if (pendingResult.success) {
      setPlatformMetrics(prev => ({
        ...prev,
        pendingVendors: pendingResult.data.length
      }));
    }

    // Load moderation queue
    const moderationResult = await adminAnalyticsService.getModerationQueue('pending');
    if (moderationResult.success) {
      setPlatformMetrics(prev => ({
        ...prev,
        reportedContent: moderationResult.data.length
      }));
    }

    // Load recent admin activity
    const logsResult = await adminAnalyticsService.getAdminLogs({ limit: 10 });
    if (logsResult.success) {
      const activities = logsResult.data.map(log => ({
        id: log.id,
        type: log.action,
        description: `${log.action.replace(/_/g, ' ')} - ${log.resource_type}`,
        user: log.admin?.full_name || 'Admin',
        timestamp: log.created_at,
        priority: 'medium'
      }));
      setRecentActivity(activities);
    }

    setLoading(false);
  };

  const [quickStats, setQuickStats] = useState({
    newUsersToday: 24,
    newVendorsToday: 3,
    bookingsToday: 8,
    revenueToday: 2400
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'users', label: 'User Management', icon: 'Users' },
    { id: 'vendors', label: 'Vendor Management', icon: 'Store' },
    { id: 'content', label: 'Content Moderation', icon: 'Shield' },
    { id: 'financial', label: 'Financial Overview', icon: 'DollarSign' },
    { id: 'analytics', label: 'Analytics', icon: 'TrendingUp' },
    { id: 'system', label: 'System Health', icon: 'Activity' },
    { id: 'categories', label: 'Category Management', icon: 'Folder' },
    { id: 'listings', label: 'Listing Management', icon: 'List' },
    { id: 'seo', label: 'SEO Tools', icon: 'Search' },
    { id: 'locations', label: 'Location Management', icon: 'MapPin' },
    { id: 'pages', label: 'Page Management', icon: 'FileText' },
    { id: 'payments', label: 'Payment Plans', icon: 'CreditCard' },
    { id: 'payment-integration', label: 'Payment Integration', icon: 'Zap' },
    { id: 'newsletter', label: 'Newsletter Tools', icon: 'Mail' },
    { id: 'backup', label: 'Backup & Restore', icon: 'Database' },
    { id: 'roles', label: 'Admin Roles', icon: 'ShieldCheck' },
    { id: 'customer-support', label: 'Customer Support', icon: 'Headphones' },
    { id: 'push-notifications', label: 'Push Notifications', icon: 'Bell' },
    { id: 'blog-management', label: 'Blog & Categories', icon: 'BookOpen' },
    { id: 'popup-manager', label: 'Pop-up Manager', icon: 'MessageSquare' },
    { id: 'testimonials', label: 'Testimonials & Reviews', icon: 'Star' },
    { id: 'email-marketing', label: 'Email Marketing', icon: 'MailOpen' },
    { id: 'events-promotions', label: 'Events & Promotions', icon: 'Calendar' },
    { id: 'settings', label: 'Settings', icon: 'Settings' }
  ];

  const handleQuickAction = (action) => {
    switch (action) {
      case 'review_vendors':
        setActiveTab('vendors');
        break;
      case 'moderate_content': setActiveTab('content');
        break;
      case 'manage_categories': setActiveTab('categories');
        break;
      case 'system_status': setActiveTab('system');
        break;
      default:
        break;
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'vendor_registration':
        return 'UserPlus';
      case 'content_report':
        return 'AlertTriangle';
      case 'payment_issue':
        return 'CreditCard';
      default:
        return 'Activity';
    }
  };

  const getActivityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-500';
      case 'medium':
        return 'text-yellow-500';
      case 'low':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Platform Overview Header */}
            <div className="bg-gradient-to-r from-primary to-secondary rounded-xl p-6 text-white">
              <h1 className="font-heading font-bold text-3xl mb-2">
                Platform Overview
              </h1>
              <p className="text-white/90">
                Manage and monitor ShaadiNest platform operations
              </p>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricsCard
                title="Total Users"
                value={platformMetrics.totalUsers.toLocaleString()}
                change={`+${quickStats.newUsersToday} today`}
                icon="Users"
                color="blue"
              />
              <MetricsCard
                title="Active Vendors"
                value={platformMetrics.activeVendors.toLocaleString()}
                change={`+${quickStats.newVendorsToday} today`}
                icon="Store"
                color="green"
              />
              <MetricsCard
                title="Monthly Bookings"
                value={platformMetrics.monthlyBookings.toLocaleString()}
                change={`+${quickStats.bookingsToday} today`}
                icon="Calendar"
                color="purple"
              />
              <MetricsCard
                title="Monthly Revenue"
                value={formatPrice(platformMetrics.totalRevenue)}
                change={`+${formatPrice(quickStats.revenueToday)} today`}
                icon="DollarSign"
                color="orange"
              />
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-4">
                Quick Actions
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Button
                  variant="outline"
                  onClick={() => handleQuickAction('review_vendors')}
                  className="h-20 flex-col"
                >
                  <Icon name="UserCheck" size={24} className="mb-2" />
                  <span>Review Vendors</span>
                  <span className="text-xs text-text-secondary">
                    {platformMetrics.pendingVendors} pending
                  </span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleQuickAction('moderate_content')}
                  className="h-20 flex-col"
                >
                  <Icon name="Shield" size={24} className="mb-2" />
                  <span>Moderate Content</span>
                  <span className="text-xs text-text-secondary">
                    {platformMetrics.reportedContent} reports
                  </span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleQuickAction('manage_categories')}
                  className="h-20 flex-col"
                >
                  <Icon name="Folder" size={24} className="mb-2" />
                  <span>Manage Categories</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleQuickAction('system_status')}
                  className="h-20 flex-col"
                >
                  <Icon name="Activity" size={24} className="mb-2" />
                  <span>System Status</span>
                  <span className="text-xs text-green-600">
                    {platformMetrics.systemUptime}% uptime
                  </span>
                </Button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-4">
                Recent Activity
              </h3>
              <div className="space-y-4">
                {recentActivity.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-center space-x-3 p-3 bg-surface-100 rounded-lg"
                  >
                    <div className={`p-2 rounded-full bg-surface-200 ${getActivityColor(activity.priority)}`}>
                      <Icon name={getActivityIcon(activity.type)} size={16} />
                    </div>
                    <div className="flex-1">
                      <p className="text-text-primary font-medium">
                        {activity.description}
                      </p>
                      <p className="text-sm text-text-secondary">
                        {activity.user} • {formatDate(activity.timestamp)}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm">
                      Review
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'users':
        return <UserManagementPanel />;
      case 'vendors':
        return <VendorManagementPanel />;
      case 'content':
        return <ContentModerationPanel />;
      case 'financial':
        return <FinancialOverview />;
      case 'analytics':
        return <AnalyticsPanel />;
      case 'system':
        return <SystemHealthPanel />;
      case 'categories':
        return <CategoryManagementPanel />;
      case 'listings':
        return <ListingManagementPanel />;
      case 'seo':
        return <SEOToolsPanel />;
      case 'locations':
        return <LocationManagementPanel />;
      case 'pages':
        return <PageManagementPanel />;
      case 'payments':
        return <PaymentPlansPanel />;
      case 'payment-integration':
        return <PaymentIntegrationPanel />;
      case 'newsletter':
        return <NewsletterToolsPanel />;
      case 'backup':
        return <BackupPanel />;
      case 'roles':
        return <AdminRolesPanel />;
      case 'customer-support':
        return <CustomerSupportPanel />;
      case 'push-notifications':
        return <PushNotificationPanel />;
      case 'blog-management':
        return <BlogManagementPanel />;
      case 'popup-manager':
        return <PopupManagerPanel />;
      case 'testimonials':
        return <TestimonialsPanel />;
      case 'email-marketing':
        return <EmailMarketingPanel />;
      case 'events-promotions':
        return <EventPromotionsPanel />;
      case 'settings':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-primary to-secondary rounded-xl p-6 text-white">
              <h2 className="font-heading font-bold text-2xl mb-2">
                Admin Settings
              </h2>
              <p className="text-white/90">
                Configure platform settings and preferences
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
                Platform Configuration
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-text-primary">Vendor Auto-Approval</h4>
                    <p className="text-sm text-text-secondary">
                      Automatically approve vendors with complete profiles
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-primary-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-text-primary">Review Moderation</h4>
                    <p className="text-sm text-text-secondary">
                      Require manual approval for all reviews
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" defaultChecked className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-primary-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Admin Dashboard | ShaadiNest</title>
        <meta name="description" content="ShaadiNest admin dashboard for platform management" />
      </Helmet>

      <Header />

      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-soft p-6 sticky top-24">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Shield" size={32} className="text-white" />
                  </div>
                  <h2 className="font-heading font-semibold text-xl text-text-primary">
                    {adminUser.name}
                  </h2>
                  <p className="text-text-secondary">{adminUser.role}</p>
                </div>

                <nav className="space-y-2 max-h-96 overflow-y-auto">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors text-sm ${
                        activeTab === tab.id
                          ? 'bg-primary text-white' :'text-text-secondary hover:bg-surface-100'
                      }`}
                    >
                      <Icon name={tab.icon} size={18} />
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminDashboard;